<template>
  <Head title="Two-Factor Authentication" />
  <div class="mx-auto w-11/12 max-w-[1200px] py-6">
    <div class="mb-6">
      <Link href="/account" class="text-navy-500 hover:text-navy-700 text-sm">
        ← Back to Account Settings
      </Link>
    </div>

    <h1 class="text-navy-800 mb-6 text-2xl font-bold">
      Two-Factor Authentication
    </h1>

    <!-- MFA Already Enabled -->
    <template v-if="mfaEnabled && !showingBackupCodes">
      <AppCard>
        <div class="mb-4 flex items-center">
          <i class="pi pi-check-circle mr-2 text-xl text-green-600"></i>
          <span class="text-navy-800 font-medium">MFA is enabled</span>
        </div>
        <p class="text-navy-500 mb-6 text-sm">
          Your account is protected with two-factor authentication. You have
          {{ backupCodesRemaining }} backup codes remaining.
        </p>
        <div class="flex gap-3">
          <AppButton :disabled="regenerating" @click="regenerateBackupCodes">
            {{ regenerating ? "Generating..." : "Regenerate Backup Codes" }}
          </AppButton>
          <AppButton
            variant="secondary"
            :disabled="disabling"
            @click="confirmDisableMfa"
          >
            {{ disabling ? "Disabling..." : "Disable MFA" }}
          </AppButton>
        </div>
      </AppCard>
    </template>

    <!-- Show Backup Codes -->
    <template v-else-if="showingBackupCodes">
      <AppCard>
        <h2 class="text-navy-800 mb-4 text-lg font-semibold">
          Save Your Backup Codes
        </h2>
        <p class="text-navy-500 mb-4 text-sm">
          Store these codes in a safe place. Each code can only be used once.
          You won't be able to see these again.
        </p>
        <div class="mb-6 grid grid-cols-2 gap-2 rounded-lg bg-neutral-50 p-4">
          <code
            v-for="code in backupCodes"
            :key="code"
            class="text-navy-800 font-mono text-sm"
          >
            {{ code }}
          </code>
        </div>
        <AppButton @click="finishSetup"> I've Saved My Codes </AppButton>
      </AppCard>
    </template>

    <!-- MFA Setup Flow -->
    <template v-else-if="!mfaEnabled">
      <!-- Step 1: Start Setup -->
      <template v-if="!setupStarted">
        <AppCard>
          <p class="text-navy-600 mb-6">
            Add an extra layer of security to your account by enabling
            two-factor authentication. You'll need an authenticator app like
            Google Authenticator or Authy.
          </p>
          <AppButton :disabled="settingUp" @click="startSetup">
            {{
              settingUp ? "Setting up..." : "Set Up Two-Factor Authentication"
            }}
          </AppButton>
        </AppCard>
      </template>

      <!-- Step 2: Scan QR Code -->
      <template v-else>
        <AppCard>
          <h2 class="text-navy-800 mb-4 text-lg font-semibold">Scan QR Code</h2>
          <p class="text-navy-500 mb-4 text-sm">
            Scan this QR code with your authenticator app.
          </p>
          <div class="mb-6 flex justify-center">
            <img :src="qrCodeUrl" alt="QR Code" class="h-48 w-48" />
          </div>

          <h2 class="text-navy-800 mb-4 text-lg font-semibold">
            Enter Verification Code
          </h2>
          <p class="text-navy-500 mb-4 text-sm">
            Enter the 6-digit code from your authenticator app to verify setup.
          </p>
          <form @submit.prevent="verifyCode">
            <input
              v-model="verificationCode"
              type="text"
              inputmode="numeric"
              maxlength="6"
              placeholder="000000"
              class="text-navy-800 mb-4 w-full rounded-md border border-neutral-300 px-4 py-3 text-center text-xl tracking-widest focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none"
              autofocus
            />
            <div v-if="verifyError" class="mb-4 text-sm text-red-600">
              {{ verifyError }}
            </div>
            <AppButton
              type="submit"
              class="w-full"
              :disabled="verifying || verificationCode.length < 6"
            >
              {{ verifying ? "Verifying..." : "Verify and Enable" }}
            </AppButton>
          </form>
        </AppCard>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { Head, Link, router } from "@inertiajs/vue3"
import { useConfirm } from "primevue/useconfirm"
import { useToast } from "primevue/usetoast"
import { computed, ref } from "vue"

import AppButton from "../../components/ui/AppButton.vue"
import AppCard from "../../components/ui/AppCard.vue"
import { apolloClient } from "../../queries/client"
import DISABLE_MFA from "../../queries/member/disable_mfa.gql"
import REGENERATE_BACKUP_CODES from "../../queries/member/regenerate_backup_codes.gql"
import SETUP_MFA from "../../queries/member/setup_mfa.gql"
import VERIFY_MFA from "../../queries/member/verify_mfa.gql"

interface Props {
  user: {
    id: string
    name: string
    email: string
    mfa_enabled: boolean
  }
  mfa_enabled: boolean
  provisioning_uri: string | null
  backup_codes_remaining: number
}

const props = defineProps<Props>()

const confirm = useConfirm()
const toast = useToast()

const mfaEnabled = ref(props.mfa_enabled)
const backupCodesRemaining = ref(props.backup_codes_remaining)

const setupStarted = ref(false)
const settingUp = ref(false)
const provisioningUri = ref(props.provisioning_uri)

const verificationCode = ref("")
const verifying = ref(false)
const verifyError = ref("")

const backupCodes = ref<string[]>([])
const showingBackupCodes = ref(false)
const regenerating = ref(false)
const disabling = ref(false)

const qrCodeUrl = computed(() => {
  if (!provisioningUri.value) return ""
  return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(provisioningUri.value)}`
})

async function startSetup() {
  settingUp.value = true
  try {
    const result = await apolloClient.mutate({ mutation: SETUP_MFA })
    if (result.data?.setupMfa?.provisioningUri) {
      provisioningUri.value = result.data.setupMfa.provisioningUri
      setupStarted.value = true
    } else {
      console.error("Setup failed:", result)
    }
  } finally {
    settingUp.value = false
  }
}

async function verifyCode() {
  verifying.value = true
  verifyError.value = ""
  try {
    const result = await apolloClient.mutate({
      mutation: VERIFY_MFA,
      variables: { code: verificationCode.value },
    })

    if (result.data?.verifyMfa?.success) {
      await generateBackupCodes()
      mfaEnabled.value = true
    } else {
      verifyError.value = result.data?.verifyMfa?.errors?.[0] || "Invalid code"
    }
  } finally {
    verifying.value = false
  }
}

async function generateBackupCodes() {
  const result = await apolloClient.mutate({
    mutation: REGENERATE_BACKUP_CODES,
  })
  if (result.data?.regenerateBackupCodes?.backupCodes) {
    backupCodes.value = result.data.regenerateBackupCodes.backupCodes
    backupCodesRemaining.value = backupCodes.value.length
    showingBackupCodes.value = true
  }
}

async function regenerateBackupCodes() {
  regenerating.value = true
  try {
    await generateBackupCodes()
  } finally {
    regenerating.value = false
  }
}

function confirmDisableMfa() {
  confirm.require({
    message:
      "Are you sure you want to disable two-factor authentication? This will make your account less secure.",
    header: "Disable MFA",
    icon: "pi pi-exclamation-triangle",
    acceptClass: "p-button-danger",
    accept: () => {
      void disableMfa()
    },
  })
}

async function disableMfa() {
  disabling.value = true
  try {
    const result = await apolloClient.mutate({ mutation: DISABLE_MFA })
    if (result.data?.disableMfa?.success) {
      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Two-factor authentication has been disabled",
        life: 4000,
      })
      mfaEnabled.value = false
      router.reload()
    } else {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: result.data?.disableMfa?.errors?.[0] || "Failed to disable MFA",
        life: 5000,
      })
    }
  } finally {
    disabling.value = false
  }
}

function finishSetup() {
  showingBackupCodes.value = false
}
</script>
